/*
 * AI-GENERATED
 * Microsoft 365 Copilot
 * LLM: GPT-5 reasoning
 * Tarkistanut Arttu Pietarinen
 * 2026-09-21
 */
import { useCallback, useContext } from 'react';
import { ReactReduxContext } from 'react-redux';
import { useAppSelector } from '../../../state/hooks';
import {
  setIndexSearchResults,
  setIsSearchingActive
} from '../../../state/slices/rpcSlice';

const DEFAULT_LIMIT = 100;

// Shared across hook instances: the search input and the result panel each call
// useIndexSearch, and only the newest request may write results to the store.
let latestRequestId = 0;

const createSearchState = ({
  status = 'idle',
  query = '',
  attribute = '',
  layerId = null,
  metadataId = null,
  items = [],
  paging = { limit: DEFAULT_LIMIT, offset: 0, returned: 0, totalCount: 0 },
  error = null,
  searchAttribute = null,
  partialMatch = true,
  serviceName = null
} = {}) => ({
  status,
  query,
  attribute,
  layerId,
  metadataId,
  items,
  paging,
  error,
  searchAttribute,
  partialMatch,
  serviceName
});

const searchIndexItems = ({
  channel,
  query,
  metadataId,
  attribute = '',
  offset = 0,
  limit = DEFAULT_LIMIT,
  searchAttribute,
  partialMatch,
  serviceName,
  includeTotalCount = true
}) =>
  new Promise((resolve, reject) => {
    if (!channel) {
      reject(new Error('Search channel is not available'));
      return;
    }

    const request = {
      target: 'items',
      query,
      limit,
      offset,
      ...(metadataId != null ? { searchMetadataId: metadataId } : {}),
      ...(attribute ? { attribute } : {}),
      searchAttribute,
      partialMatch,
      includeTotalCount: includeTotalCount,
      serviceName
    };

    channel.searchIndex([request], (response, error) => {
      if (error) {
        reject(error);
        return;
      }

      if (response?.target !== 'items') {
        reject(
          new Error(`Unexpected search response target: ${response?.target}`)
        );
        return;
      }

      resolve(response);
    });
  });

const searchIndexFilters = ({
  channel,
  filters,
  metadataId,
  offset = 0,
  limit = DEFAULT_LIMIT,
  serviceName,
  includeGeometry = true,
  includeTotalCount = true,
  returnOnlyIds = false
}) =>
  new Promise((resolve, reject) => {
    if (!channel) {
      reject(new Error('Search channel is not available'));
      return;
    }

    const request = {
      target: 'filters',
      searchMetadataId: metadataId,
      filters,
      limit,
      offset,
      includeGeometry,
      includeTotalCount,
      returnOnlyIds,
      serviceName
    };

    console.info('[LayerFilter API] Request', request);

    channel.searchIndex([request], (response, error) => {
      if (error) {
        console.error('[LayerFilter API] Error', error);
        reject(error);
        return;
      }

      if (response?.target !== 'filters') {
        const unexpectedTargetError = new Error(
          `Unexpected filter search response target: ${response?.target}`
        );
        console.error('[LayerFilter API] Error', unexpectedTargetError);
        reject(unexpectedTargetError);
        return;
      }

      console.info('[LayerFilter API] Response', response);
      console.info('[LayerFilter API] Results', response.results ?? []);
      resolve(response);
    });
  });

const useIndexSearch = () => {
  const { store } = useContext(ReactReduxContext);
  const channel = useAppSelector((state) => state.rpc.channel);
  const indexSearchResults = useAppSelector(
    (state) => state.rpc.indexSearchResults
  );

  const executeIndexSearch = useCallback(
    async ({
      query,
      attribute = '',
      layerId = null,
      metadataId = null,
      offset = 0,
      searchAttribute = null,
      partialMatch = true,
      serviceName = null
    }) => {
      const requestId = ++latestRequestId;
      const append = offset > 0;
      const previousItems =
        append && Array.isArray(indexSearchResults?.items)
          ? indexSearchResults.items
          : [];

      const limit = indexSearchResults?.paging?.limit ?? DEFAULT_LIMIT;

      const defaultPaging = {
        limit,
        offset,
        returned: 0,
        totalCount: 0,
        hasMoreResults: false
      };

      store.dispatch(
        setIndexSearchResults(
          createSearchState({
            status: 'loading',
            query,
            attribute,
            layerId,
            metadataId,
            items: previousItems,
            paging: defaultPaging,
            searchAttribute,
            partialMatch,
            serviceName
          })
        )
      );

      try {
        const response = await searchIndexItems({
          channel,
          query,
          metadataId,
          attribute,
          offset,
          limit,
          searchAttribute,
          partialMatch,
          includeTotalCount: true,
          serviceName
        });

        if (requestId !== latestRequestId) return;

        const returnedItems = response.results ?? [];

        store.dispatch(
          setIndexSearchResults(
            createSearchState({
              status: 'succeeded',
              query,
              attribute,
              layerId,
              metadataId,
              items: append
                ? [...previousItems, ...returnedItems]
                : returnedItems,
              paging: response.paging ?? {
                ...defaultPaging,
                returned: returnedItems.length
              },
              searchAttribute,
              partialMatch,
              serviceName
            })
          )
        );

        store.dispatch(setIsSearchingActive(false));
      } catch (error) {
        if (requestId !== latestRequestId) return;
        store.dispatch(
          setIndexSearchResults(
            createSearchState({
              status: 'failed',
              query,
              attribute,
              layerId,
              metadataId,
              items: previousItems,
              paging: defaultPaging,
              error: error instanceof Error ? error.message : String(error),
              searchAttribute,
              partialMatch
            })
          )
        );
      }
    },
    [
      channel,
      indexSearchResults?.items,
      indexSearchResults?.paging?.limit,
      store
    ]
  );

  const executeFilterSearch = useCallback(
    async ({
      filters,
      metadataId,
      offset = 0,
      limit = DEFAULT_LIMIT,
      serviceName = null,
      includeGeometry = true,
      includeTotalCount = true,
      returnOnlyIds = false
    }) => {
      if (!Number.isSafeInteger(metadataId) || metadataId <= 0) {
        throw new Error(
          'A positive searchMetadataId is required for a filter search'
        );
      }

      if (!Array.isArray(filters) || filters.length === 0) {
        throw new Error('At least one filter is required for a filter search');
      }

      return searchIndexFilters({
        channel,
        filters,
        metadataId,
        offset,
        limit,
        serviceName,
        includeGeometry,
        includeTotalCount,
        returnOnlyIds
      });
    },
    [channel]
  );

  const loadNextPage = useCallback(() => {
    const {
      status,
      query,
      attribute,
      layerId,
      metadataId,
      paging,
      searchAttribute,
      partialMatch,
      serviceName
    } = indexSearchResults ?? {};

    if (status === 'loading' || !query || !paging) {
      return;
    }

    const nextOffset = paging.offset + paging.returned;

    return executeIndexSearch({
      query,
      attribute,
      layerId,
      metadataId,
      offset: nextOffset,
      searchAttribute,
      partialMatch,
      includeTotalCount: true,
      serviceName
    });
  }, [executeIndexSearch, indexSearchResults]);

  return {
    executeIndexSearch,
    executeFilterSearch,
    loadNextPage
  };
};

export default useIndexSearch;
